import React, { useState } from 'react';
import GitChanges from './GitChanges';
import GitHistory from './GitHistory';
import GitDetailTabs from './GitDetailTabs';
import GitConnectEmpty from './GitConnectEmpty';
import GitScopeHead from './GitScopeHead';
import GitScopeChanges from './GitScopeChanges';
import { useScopeGitHost } from './GitScopeHost';
import { LinkedRow, FolderRow, UnlinkedRow } from './GitWorkspaceRow';
import { LocalRow } from './GitLocalRow';
import { TEAM_NOT_LOCAL } from '../../services/git/scopeLocalGitSync';

/** A workspace synced or tracked: Changes and History, reloaded after a version is put back. */
function WorkspaceTabs({ workspace, waiting, syncing, onLoadChanges, onPutBack }) {
  const [putBacks, setPutBacks] = useState(0);
  const putBack = onPutBack
    ? async (...args) => {
        await onPutBack(...args);
        setPutBacks((n) => n + 1);
      }
    : null;
  return (
    <GitDetailTabs
      waiting={waiting}
      changes={
        onLoadChanges ? (
          <GitChanges key={`c${putBacks}`} workspace={workspace} onLoadChanges={onLoadChanges} onPutBack={putBack} syncing={syncing} />
        ) : null
      }
      renderHistory={(onCount) => <GitHistory key={`h${putBacks}`} workspace={workspace} onPutBack={putBack} syncing={syncing} onCount={onCount} />}
    />
  );
}

function ScopeBody({ target }) {
  const host = useScopeGitHost();
  const { scope } = target;
  const busy = host.busyKey === scope.key;
  if (scope.git) return <GitScopeChanges scope={scope} busy={busy} waiting={target.waiting} />;
  if (scope.kind === 'team' && scope.teamRepo) return null; // the head's Join is the way in
  const locked = scope.kind === 'team' && !scope.canConnect;
  return (
    <GitConnectEmpty
      text={
        scope.kind === 'team'
          ? `Keep ${scope.name}’s projects, boards and documents in a repository the whole team syncs with.`
          : 'Keep your projects, boards and documents in your own repository — versioned, on every device, with their history here.'
      }
      onConnect={() => host.openConnect(scope)}
      disabled={busy || locked}
      hint={scope.kind === 'team' ? TEAM_NOT_LOCAL : ''}
    />
  );
}

/**
 * The Git page's detail card (redesign 05.10.2026): the selected workspace or
 * scope — its head, then Changes | History, or the way to connect it.
 */
export default function GitDetail({ target, byWorkspace, gitSyncingWorkspaceId, onGitSync, onGitPull, onGitAddRemote, onGitStopTracking, onLoadChanges, onPutBack }) {
  if (!target) return null;
  if (target.kind === 'scope') {
    const { scope } = target;
    // Not connected: the head and its Connect card are one row, as the list row was.
    const notConnected = !scope.git && !(scope.kind === 'team' && scope.teamRepo);
    const content = (
      <>
        <GitScopeHead scope={scope} />
        <ScopeBody target={target} />
      </>
    );
    return (
      <main className="git-detail" data-testid="git-detail" data-git-target={target.id}>
        {notConnected ? (
          <div data-testid="git-scope-row" data-git-scope={scope.key}>
            {content}
          </div>
        ) : (
          content
        )}
      </main>
    );
  }

  const ws = target.workspace;
  const entry = byWorkspace[String(ws.id)];
  const syncing = String(gitSyncingWorkspaceId) === String(ws.id);
  let head;
  let body = null;
  if (target.kind === 'linked') head = <LinkedRow workspace={ws} entry={entry} syncing={syncing} onSync={onGitSync} onPull={onGitPull} />;
  if (target.kind === 'local') {
    head = <LocalRow workspace={ws} entry={entry} syncing={syncing} onSave={onGitSync} onAddRemote={onGitAddRemote} onStopTracking={onGitStopTracking} />;
  }
  if (target.kind === 'linked' || target.kind === 'local') {
    body = <WorkspaceTabs workspace={ws} waiting={target.waiting} syncing={syncing} onLoadChanges={onLoadChanges} onPutBack={onPutBack} />;
  }
  if (target.kind === 'folder') {
    head = <FolderRow workspace={ws} />;
    body = (
      <p className="git-detail-note">
        Apiboo keeps this workspace’s files in this folder. Commit and push them with your own git tools — next to your code.
      </p>
    );
  }
  if (target.kind === 'unlinked') {
    // The head and its Connect card are one row, as the list row was.
    head = (
      <div data-testid="git-unlinked-row" data-git-ws={ws.id}>
        <UnlinkedRow workspace={ws} />
        <GitConnectEmpty
          text="Connect it to a GitHub, GitLab or other git repository to version it, use it on every device and see its history here."
          onConnect={() => onGitSync(ws)}
          disabled={syncing}
        />
      </div>
    );
  }
  return (
    <main className="git-detail" data-testid="git-detail" data-git-target={target.id}>
      {head}
      {body}
    </main>
  );
}
